import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/nb";
import { User, UserPlus, X } from "lucide-react";
import { FormProvider } from "react-hook-form";

import { FormFieldGroup } from "../../../components";
import { useMeetingMinutesForm } from "./hooks/useMeetingMinutesForm";
import { flattenMeetingMinutes } from "./MeetingMinutesForm.rules";
import { MeetingMinutesFormProps } from "./MeetingMinutesForm.types";
import { QueryContact } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { cn } from "@/lib/utils";

dayjs.extend(customParseFormat);

// Same field styling as the order form.
const fieldCls =
  "w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12px] text-foreground shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/15";

const muiFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 12 },
  "& .MuiPickersInputBase-sectionsContainer": {
    paddingTop: "6px",
    paddingBottom: "6px",
  },
};

function errMsg(error?: { message?: unknown }) {
  return typeof error?.message === "string" ? error.message : undefined;
}

function SubHeading({ title }: { title: string }) {
  return (
    <div className="mb-2.5 flex items-center gap-2">
      <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {title}
      </span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  );
}

export function MeetingMinutesForm({
  onDocumentCreated,
  onClose,
  contactName,
  advisorName,
  initialData,
}: MeetingMinutesFormProps) {
  const form = useMeetingMinutesForm(initialData);
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form;

  const [meetDateTime, setMeetDateTime] = useState<Dayjs | null>(() => {
    if (!initialData) return dayjs();
    const parsed = dayjs(
      form.getValues("docRefMeetDate"),
      "DD.MM.YYYY HH:mm",
      true
    );
    return parsed.isValid() ? parsed : null;
  });

  // Participants kept as a list internally; serialized to comma-separated
  // string in `docRefMeetPart` (same output format as before).
  const [participants, setParticipants] = useState<string[]>(() =>
    initialData
      ? form
          .getValues("docRefMeetPart")
          .split(",")
          .map((name: string) => name.trim())
          .filter(Boolean)
      : [contactName, advisorName].filter((name: string | undefined): name is string =>
          Boolean(name)
        )
  );
  const [participantInput, setParticipantInput] = useState("");

  const { data: contactsData, loading: loadingContacts } =
    useGetSavedQuery2<QueryContact>("advPortalContactsSimple", {
      contactName: "%",
    });

  const contactOptions = useMemo(() => {
    const list = (contactsData || [])
      .map((c) => c.name)
      .filter((n): n is string => Boolean(n));
    return Array.from(new Set(list)).sort((a, b) =>
      a.localeCompare(b, "no", { sensitivity: "base" })
    );
  }, [contactsData]);

  const addParticipant = (raw: string) => {
    const name = raw.trim();
    if (!name) return;
    setParticipants((prev) =>
      prev.some((p) => p.toLowerCase() === name.toLowerCase())
        ? prev
        : [...prev, name]
    );
    setParticipantInput("");
  };

  const removeParticipant = (name: string) => {
    setParticipants((prev) => prev.filter((p) => p !== name));
  };

  // Keep form value in sync with the chip list
  useEffect(() => {
    setValue("docRefMeetPart", participants.join(", "));
  }, [participants, setValue]);

  const syncDateTime = (dt: Dayjs) => {
    setValue("docRefMeetDate", dt.format("DD.MM.YYYY HH:mm"));
  };

  // Set initial value on mount
  useEffect(() => {
    if (!initialData) {
      setValue(
        "docRefMeetDate",
        meetDateTime?.format("DD.MM.YYYY HH:mm") ?? ""
      );
      setValue("docRefMeetPlace", "Oslo");
    }
    // participants are seeded from props via initial state above
    // only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data: Parameters<typeof flattenMeetingMinutes>[0]) => {
    onDocumentCreated?.(flattenMeetingMinutes(data));
    onClose?.();
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full min-h-0 flex-col"
      >
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 p-4 sm:p-5">
          <div className="mx-auto max-w-3xl divide-y divide-border/60 overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm">
            <div className="p-4">
              <SubHeading title="Møtedetaljer" />
              <div className="grid gap-3 sm:grid-cols-2">
                <FormFieldGroup
                  label="Dato og tid"
                  error={errMsg(errors.docRefMeetDate)}
                >
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="nb"
                  >
                    <DateTimePicker
                      value={meetDateTime}
                      onChange={(d) => {
                        if (d?.isValid()) {
                          setMeetDateTime(d);
                          syncDateTime(d);
                        }
                      }}
                      format="DD.MM.YYYY HH:mm"
                      ampm={false}
                      slotProps={{
                        textField: { size: "small", sx: muiFieldSx },
                      }}
                    />
                  </LocalizationProvider>
                </FormFieldGroup>

                <FormFieldGroup
                  label="Sted"
                  error={errMsg(errors.docRefMeetPlace)}
                >
                  <input
                    type="text"
                    {...register("docRefMeetPlace")}
                    className={fieldCls}
                  />
                </FormFieldGroup>
              </div>
            </div>

            <div className="p-4">
              <SubHeading title="Deltakere" />
              <div className="space-y-2">
                {participants.length > 0 && (
                  <ul className="flex flex-wrap gap-1.5">
                    {participants.map((name) => (
                      <li
                        key={name}
                        className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 py-0.5 pl-2 pr-1 text-xs font-medium text-primary"
                      >
                        <User className="h-3 w-3 opacity-70" />
                        <span className="max-w-[200px] truncate">{name}</span>
                        <button
                          type="button"
                          onClick={() => removeParticipant(name)}
                          aria-label={`Fjern ${name}`}
                          className="ml-0.5 flex size-4 items-center justify-center rounded-full text-primary/70 transition-colors hover:bg-primary/15 hover:text-primary"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <Autocomplete<string, false, false, true>
                  freeSolo
                  size="small"
                  options={contactOptions}
                  loading={loadingContacts}
                  inputValue={participantInput}
                  onInputChange={(_, v, reason) => {
                    if (reason !== "reset") setParticipantInput(v);
                  }}
                  filterOptions={(opts, state) => {
                    const q = state.inputValue.trim().toLowerCase();
                    const selected = new Set(
                      participants.map((p) => p.toLowerCase())
                    );
                    const filtered = opts.filter(
                      (o) =>
                        !selected.has(o.toLowerCase()) &&
                        (!q || o.toLowerCase().includes(q))
                    );
                    return filtered.slice(0, 50);
                  }}
                  onChange={(_, value) => {
                    if (typeof value === "string") addParticipant(value);
                  }}
                  sx={muiFieldSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Søk i kontakter eller skriv inn et navn…"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && participantInput.trim()) {
                          e.preventDefault();
                          addParticipant(participantInput);
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <UserPlus className="ml-1 mr-0.5 size-3.5 text-muted-foreground" />
                        ),
                      }}
                    />
                  )}
                />

                {/* Hidden bound field so RHF keeps the value in form state */}
                <input type="hidden" {...register("docRefMeetPart")} />
              </div>
            </div>

            <div className="p-4">
              <SubHeading title="Referat" />
              <FormFieldGroup label="Møtereferat">
                <textarea
                  {...register("docRefDet")}
                  rows={8}
                  className={cn(fieldCls, "resize-y")}
                />
              </FormFieldGroup>
            </div>
          </div>

          {Object.keys(errors).length > 0 && (
            <p className="mx-auto mt-3 max-w-3xl text-[11px] font-medium text-destructive">
              Fyll ut alle obligatoriske felt.
            </p>
          )}
        </div>

        <div className="z-10 flex shrink-0 justify-end gap-3 border-t border-border bg-background px-4 py-3">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
            >
              Avbryt
            </button>
          )}
          <button
            type="submit"
            className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
          >
            {initialData ? "Lagre endringer" : "Legg til dokument"}
          </button>
        </div>
      </form>
    </FormProvider>
  );
}
